/**
 * Customer-Specific Navigation
 * Maps customer types to their specific navigation configuration
 */

import { Customer, CustomerType, isCondominio } from '../../types/customer';
import { NavGroup } from './types';
import { getCondominioNavigation } from './condominio';
import { getEmpresaNavigation } from './empresa';

/**
 * Get navigation configuration for a specific customer
 * @param customer - Full customer object (null for fallback)
 * @returns Navigation configuration with dynamic labels
 */
export function getCustomerNavigation(customer: Customer | null): NavGroup[] {
  if (!customer) {
    // Fallback to default condominio navigation if no customer
    return getCondominioNavigation('apartamento');
  }

  // For condominios, use dynamic navigation based on condoType
  if (isCondominio(customer)) {
    return getCondominioNavigation(customer.condoType, customer);
  }

  // For other customer types
  const navigationMap: Record<CustomerType, NavGroup[]> = {
    condominio: getCondominioNavigation('apartamento', customer), // Fallback
    empresa: getEmpresaNavigation(customer),
    fabrica: getEmpresaNavigation(customer), // Fabrica uses empresa navigation
  };

  return navigationMap[customer.type] || [];
}

/**
 * Check if a navigation item should be visible based on user role level
 */
export function isNavigationItemVisible(
  item: NavGroup,
  userRoleLevel: number
): boolean {
  if (item.minRoleLevel !== undefined) {
    return userRoleLevel >= item.minRoleLevel;
  }
  return true;
}

/**
 * Filter navigation based on user permissions
 */
export function filterNavigationByPermissions(
  navigation: NavGroup[],
  userRoleLevel: number
): NavGroup[] {
  return navigation.filter(group => isNavigationItemVisible(group, userRoleLevel));
}
